import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { menuApi } from '../../menu-management/api/menuApi';
import type { MyMenuItem } from '../../menu-management/types/menu';
import type { NavGroup, UserInfo } from '@ui-kit';
import { useMe } from './useMe';

/**
 * DB menuKey → GROUP_ORDER キー 매핑.
 * depth-0 DIVIDER 노드의 menuKey 를 사이드바 그룹 slug/i18n 키 로 변환한다.
 *
 * #375: FE 사이드바 source 를 /biz/catalog (yml) → /menus/me (DB SSOT) 로 전환.
 * BE 가 role 필터 후 응답하므로 FE 추가 role 필터 불필요.
 */
const MENU_KEY_TO_GROUP: Record<string, { key: string; i18nKey: string; slug: string }> = {
  home:               { key: '홈',        i18nKey: 'nav.group.home',      slug: 'home'      },
  system:             { key: '시스템',    i18nKey: 'nav.group.system',    slug: 'system'    },
  business:           { key: '업무',      i18nKey: 'nav.group.business',  slug: 'business'  },
  devtools:           { key: '개발자도구', i18nKey: 'nav.group.devtools',  slug: 'devtools'  },
  external_resources: { key: '외부리소스', i18nKey: 'nav.group.external',  slug: 'external'  },
};

/** 그룹 표시 순서 (DB 응답 순서와 독립적으로 고정 순서 보장) */
const GROUP_ORDER_KEYS = ['home', 'business', 'devtools', 'system', 'external_resources'];

/**
 * /api/eraf/menus/me 응답(DB SSOT) → NavGroup[] 변환 어댑터.
 *
 * depth-0 노드: DIVIDER — 그룹 헤더.
 * depth-1 노드: INTERNAL/EXTERNAL — 실제 사이드바 항목.
 * BE 가 사용자 role 기준으로 이미 필터링했으므로 FE 추가 필터 없음.
 */
function adaptMyMenusToNavGroups(
  myMenus: MyMenuItem[],
  t: (key: string, opts?: { defaultValue?: string }) => string,
  lang: string
): NavGroup[] {
  // depth-0 루트만 그룹으로 사용 (children = 실제 항목)
  const roots = myMenus.filter((m) => m.depth === 0);

  // 현재 로케일 기준 fallback 라벨 결정 (EN 토글 시 nameEn 우선)
  const isEn = lang.toLowerCase().startsWith('en');
  const resolveFallback = (item: { nameEn?: string | null; nameKo: string }): string =>
    isEn ? (item.nameEn?.trim() ? item.nameEn : item.nameKo) : item.nameKo;

  const groups: NavGroup[] = [];

  for (const groupKey of GROUP_ORDER_KEYS) {
    const rootNode = roots.find((r) => r.menuKey === groupKey);
    if (!rootNode) continue;

    const groupMeta = MENU_KEY_TO_GROUP[groupKey];
    if (!groupMeta) continue;

    // children 을 sortOrder ASC 로 정렬 후 NavItem 으로 변환
    const children = [...(rootNode.children ?? [])].sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
    );

    const navItems = children
      .filter((child) => child.menuType !== 'DIVIDER' && child.url)
      .map((child) => {
        // labelKey 우선, 없으면 nav.item.<menuKey> i18n 키, 최종 fallback 은 로케일 기준 nameEn/nameKo
        const fallback = resolveFallback(child);
        const label = child.labelKey
          ? t(child.labelKey, { defaultValue: fallback })
          : t(`nav.item.${child.menuKey}`, { defaultValue: fallback });

        return {
          key: child.menuKey,
          label,
          path: child.url!,
          icon: child.icon ?? undefined,
        };
      });

    if (navItems.length === 0) continue;

    groups.push({
      key: groupMeta.key,
      slug: groupMeta.slug,
      label: t(groupMeta.i18nKey, { defaultValue: groupMeta.key }),
      items: navItems,
    });
  }

  return groups;
}

export interface UseNavMenuResult {
  groups: NavGroup[];
  currentUser: UserInfo | null;
  isLoading: boolean;
}

/**
 * /api/eraf/menus/me (DB SSOT) + useMe() → NavGroup[] 변환.
 *
 * 변경 이력:
 *   - 초기: /biz/catalog (application.yml) 기반
 *   - #375 R14: /menus/me (DB) 로 전환 — 메뉴 편집 즉시 사이드바 반영 보장.
 *     /biz/catalog 는 _index 카탈로그 표시용으로 유지 (사이드바와 무관).
 *
 * queryKey: ['menu-management', 'me'] + ['auth', 'me']
 */
export function useNavMenu(): UseNavMenuResult {
  const { t, i18n } = useTranslation();
  const meQuery = useMe();

  const myMenusQuery = useQuery<MyMenuItem[]>({
    queryKey: ['menu-management', 'me'],
    queryFn: () => menuApi.getMyMenus(),
    staleTime: 30_000, // 30초 — 메뉴 편집 후 빠른 반영
    enabled: !!meQuery.data,  // 인증 확인 후 호출
  });

  /** 대시보드(_index) 고정 항목 — 홈 그룹 최상단에 항상 노출 (fallback 안전망) */
  const dashboardItem = {
    key: '_index',
    label: t('nav.item._index', { defaultValue: '대시보드' }),
    path: '/_index',
    icon: 'home',
  };

  /** 정적 홈 그룹 — myMenus 로드 전 또는 홈 그룹 미포함 시 폴백 */
  const staticHomeGroup: NavGroup = {
    key: '홈',
    slug: 'home',
    label: t('nav.group.home', { defaultValue: '홈' }),
    items: [dashboardItem],
  };

  const me = meQuery.data;

  if (!me) {
    return {
      groups: [staticHomeGroup],
      currentUser: null,
      isLoading: meQuery.isLoading || myMenusQuery.isLoading,
    };
  }

  const currentUser: UserInfo = {
    id: me.id,
    displayName: me.displayName,
    email: me.email,
    role: (me.role as UserInfo['role']) ?? 'ROLE_USER',
  };

  if (!myMenusQuery.data) {
    return {
      groups: [staticHomeGroup],
      currentUser,
      isLoading: myMenusQuery.isLoading,
    };
  }

  // DB 응답 → NavGroup[] 변환 (로케일 기준 nameEn/nameKo fallback · i18n 미초기화 시 ko)
  const dynamicGroups = adaptMyMenusToNavGroups(myMenusQuery.data, t, i18n?.language ?? 'ko');

  // 홈 그룹에 _index(대시보드) 가 없으면 첫 번째 항목으로 강제 삽입
  // DB 시드(V20263302) 에 이미 포함되어 있어야 하지만 방어적 fallback 유지
  const homeGroupIdx = dynamicGroups.findIndex((g) => g.key === '홈');
  let groups: NavGroup[];
  if (homeGroupIdx === -1) {
    // 홈 그룹 자체가 없으면 정적 홈 그룹을 맨 앞에 삽입
    groups = [staticHomeGroup, ...dynamicGroups];
  } else {
    const homeGroup = dynamicGroups[homeGroupIdx];
    const hasDashboard = homeGroup.items.some((item) => item.key === '_index');
    if (!hasDashboard) {
      // 대시보드를 첫 번째로 삽입
      dynamicGroups[homeGroupIdx] = {
        ...homeGroup,
        items: [dashboardItem, ...homeGroup.items],
      };
    }
    groups = dynamicGroups;
  }

  return {
    groups,
    currentUser,
    isLoading: false,
  };
}
